/**
 * PROPRIETARY AND CONFIDENTIAL
 * Copyright © 2026 Contractor Co-Pilot. All Rights Reserved.
 */

/**
 * useColorScale Hook
 *
 * React hook for accessing dynamic color scales from CSS variables.
 * Provides reactive access to organization-branded colors.
 */

import { getColorScaleValue } from "@/lib/utils/colors/color-scale-utils";
import { useMemo } from "react";

/**
 * Hook to access dynamic color scale values
 *
 * @example
 * ```tsx
 * const { getShade } = useColorScale();
 * const lightBg = getShade('primary', 50); // hsl(var(--primary-50))
 * const mainColor = getShade('primary', 500); // hsl(var(--primary-500))
 * ```
 */
export function useColorScale() {
  return useMemo(() => {
    /**
     * Get a color shade value
     * @param scale - Color scale name (primary, secondary, success, etc.)
     * @param shade - Shade number (50, 100, 200, ..., 950)
     * @param fallback - Fallback hex color if CSS variable is not available
     * @returns HSL format string (e.g., "35 51% 47%")
     */
    const getShade = (
      scale: "primary" | "secondary" | "success" | "warning" | "error" | "info",
      shade: number,
      fallback: string,
    ): string => {
      return getColorScaleValue(scale, shade, fallback);
    };

    /**
     * Get a color shade as a full HSL string for use in inline styles
     */
    const getShadeHsl = (
      scale: "primary" | "secondary" | "success" | "warning" | "error" | "info",
      shade: number,
      fallback: string,
    ): string => {
      const hslValue = getShade(scale, shade, fallback);
      return `hsl(${hslValue})`;
    };

    return {
      getShade,
      getShadeHsl,
    };
  }, []);
}
